/**
 * Transforms the rich `AttributionContext` from localStorage into the flat
 * key-value object expected by the funnel event envelope's `attribution`
 * field and by the session creation endpoint.
 *
 * The mapping extracts `lastTouch` fields (UTMs, platform details, click IDs,
 * pixel IDs) into the snake_case keys defined in the Zod
 * `attributionSchema` of `@nextday/funnel-events`.
 */
import type { AttributionContext, TouchPoint } from "@nextday/attribution";

export type FlatAttribution = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  campaign_id?: string;
  /** D1 inlet row id — injected by the SPA after resolving the inlet. */
  inlet_id?: string;
  adset_id?: string;
  ad_id?: string;
  placement?: string;
  site_source_name?: string;
  account_id?: string;
  network?: string;
  click_id?: string;
  fbclid?: string;
  fbc?: string;
  fbp?: string;
  first_touch_at?: string;
  last_touch_at?: string;
  affiliate_id?: string;
  tap_vid?: string;
  crak_revenue_click_id?: string;
  partner?: string;
};

const extractUtm = (lt: TouchPoint, out: FlatAttribution): void => {
  const utm = lt.utm;
  if (utm?.source) out.utm_source = utm.source;
  if (utm?.medium) out.utm_medium = utm.medium;
  if (utm?.campaign) out.utm_campaign = utm.campaign;
  if (utm?.content) out.utm_content = utm.content;
  if (utm?.term) out.utm_term = utm.term;
};

const extractPlatformIds = (lt: TouchPoint, out: FlatAttribution): void => {
  const pd = lt.platformDetails;
  const nids = lt.networkIds;
  const cid = pd?.campaignId || nids?.campaignId;
  const aid = pd?.adId || nids?.adId;
  const asid = pd?.adGroupId || nids?.adGroupId;
  if (cid) out.campaign_id = cid;
  if (aid) out.ad_id = aid;
  if (asid) out.adset_id = asid;
  // account_id / placement handled in extractPlatformMeta (blob12/blob16 swap)
};

/** Pre-NX-10282 ExoClick inlets whose live tracking URLs use account_id={zone_id}. */
const LEGACY_EXOCLICK_SLUGS = ["exo_banner", "exo_hentaicity", "exo_native", "exo_popunder"];
const NUMERIC_RE = /^\d+$/;

const isLegacyExoClickInlet = (lt: TouchPoint): boolean => {
  const url = lt.landingUrl;
  if (!url) return false;
  return LEGACY_EXOCLICK_SLUGS.some((slug) => url.includes(`/${slug}`));
};

const extractPlatformMeta = (lt: TouchPoint, out: FlatAttribution): void => {
  const pd = lt.platformDetails;
  if (pd?.source) out.site_source_name = pd.source;
  if (lt.network && lt.network !== "unknown") out.network = lt.network;

  // blob12/blob16 swap (NX-10282):
  //   blob12 = account_id (network account slug, e.g. "exo_main")
  //   blob16 = placement  (zone/site ID where the ad ran)
  //
  // Legacy ExoClick tracking URLs have account_id={zone_id} (numeric).
  // New tracking URLs have placement={zone_id} + account_id=<slug>.
  // Only remap for the 4 pre-existing ExoClick inlets.
  const rawAccountId = pd?.accountId || lt.networkIds?.accountId;
  const rawPlacement = pd?.placement;

  if (isLegacyExoClickInlet(lt) && rawAccountId && NUMERIC_RE.test(rawAccountId)) {
    out.placement = rawAccountId;
  } else {
    if (rawAccountId) out.account_id = rawAccountId;
    if (rawPlacement) out.placement = rawPlacement;
  }
};

const extractClickIds = (lt: TouchPoint, out: FlatAttribution): void => {
  if (lt.primaryClickRef?.value) out.click_id = lt.primaryClickRef.value;
  if (lt.clickIds?.fbclid) out.fbclid = lt.clickIds.fbclid;
  if (lt.pixelIds?.fbc) out.fbc = lt.pixelIds.fbc;
  if (lt.pixelIds?.fbp) out.fbp = lt.pixelIds.fbp;
};

const extractPartners = (ctx: AttributionContext, out: FlatAttribution): void => {
  const p = ctx.partners;
  if (!p) return;
  if (p.affiliateId) out.affiliate_id = p.affiliateId;
  if (p.tapVid) out.tap_vid = p.tapVid;
  if (p.crakRevenueClickId) out.crak_revenue_click_id = p.crakRevenueClickId;
  if (p.partner) out.partner = p.partner;
};

const hasDefinedValues = (obj: FlatAttribution): boolean =>
  Object.values(obj).some((v) => v !== undefined);

const clean = (obj: FlatAttribution): FlatAttribution => {
  const out: FlatAttribution = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) (out as Record<string, string>)[k] = v;
  }
  return out;
};

export const flattenAttribution = (
  ctx: AttributionContext | undefined,
): FlatAttribution | undefined => {
  if (!ctx) return undefined;

  const lt = ctx.lastTouch;
  if (!lt) return undefined;

  const flat: FlatAttribution = {};

  extractUtm(lt, flat);
  extractPlatformIds(lt, flat);
  extractPlatformMeta(lt, flat);
  extractClickIds(lt, flat);
  extractPartners(ctx, flat);

  if (ctx.firstTouch?.capturedAt)
    flat.first_touch_at = new Date(ctx.firstTouch.capturedAt).toISOString();
  if (lt.capturedAt) flat.last_touch_at = new Date(lt.capturedAt).toISOString();

  return hasDefinedValues(flat) ? clean(flat) : undefined;
};
